import moment from 'moment';
import React, { useContext } from 'react';

import {
  Text,
  Flex,
  Spinner,
  Tooltip,
  IconButton,
  UndoIcon,
  type FlexProps,
} from '@frontend/legacy-components';
import { useDateFormat } from '@frontend/utils';

import { formatFloat } from 'utils/formatters';
import { isBlank } from 'utils/helpers';
import { SPECIAL_VARIABLE_KEYS } from 'utils/quoteUtils';

import CellContainer from './CellContainer';
import { TableContext } from './Table';

type Audit = {
  firstName: string;
  lastName: string;
  timestamp: string;
};

export interface CellProps extends FlexProps {
  name: string;
  column: number;
  editableComponent?: React.ComponentType<unknown>;
  toolTipText?: string;
  isEdited?: boolean;
  isLoading?: boolean;
  leftElement?: React.ReactNode | null;
  audit?: Audit;
  onSave: unknown;
  // Context value overrides
  big?: boolean;
  dark?: boolean;
  editable?: boolean;
  revertable?: boolean;
  shouldHighlightOverrides?: boolean;
  forceDefaultWhenBlank?: boolean;
  showSubValue?: boolean;
  // Source keys
  overrideKey?: string;
  manualKey?: string;
  subManualKey?: string;
  calculatedKey?: string;
  subCalculatedKey?: string;
  source?: Record<string, string | number | null>;
  // Formatting, validating, parsing
  formatter: unknown;
  unitFormatter: unknown;
  validator: unknown;
  parser: unknown;
}

const identity = (x: unknown) => x;

const Cell = ({
  column,
  calculatedKey,
  manualKey,
  overrideKey,
  isEdited,
  subCalculatedKey = 'unitPrice',
  subManualKey,
  editable,
  editableComponent,
  toolTipText,
  audit,
  formatter = identity,
  unitFormatter = formatter,
  name,
  onSave,
  validator,
  parser,
  revertable,
  shouldHighlightOverrides,
  showSubValue,
  source,
  forceDefaultWhenBlank,
  isLoading,
  leftElement,
  ...rest
}: CellProps) => {
  const tableContext = useContext(TableContext);
  const isEditable = editable ?? tableContext.editable;
  const isRevertable = revertable ?? tableContext.revertable;
  const highlightOverrides =
    shouldHighlightOverrides ?? tableContext.shouldHighlightOverrides;
  const shouldForceDefaultWhenBlank =
    forceDefaultWhenBlank ?? tableContext.forceDefaultWhenBlank;

  const value =
    source?.[overrideKey] ?? source?.[manualKey] ?? source?.[calculatedKey];
  const subValue = source?.[subManualKey] ?? source?.[subCalculatedKey];
  const edited =
    isEdited ||
    !isBlank(source?.[manualKey]) ||
    !isBlank(source?.[subManualKey]);
  const shouldShowRevertIcon =
    !isLoading && isRevertable && edited && isEditable;
  const shouldShowSubValue =
    !isLoading &&
    value != null &&
    subValue != null &&
    (showSubValue ?? tableContext.showSubValue);

  const handleSave = (value) => {
    if (
      name === SPECIAL_VARIABLE_KEYS.SETUP_TIME ||
      name === SPECIAL_VARIABLE_KEYS.RUNTIME
    ) {
      onSave(value);
    } else {
      const truncatedValue = formatFloat(value, 6, true);
      onSave(truncatedValue);
    }
  };
  const { dateFormat } = useDateFormat();

  const dateStringToString = (timestamp: string) =>
    moment
      .utc(timestamp)
      .local()
      .calendar({
        sameDay: '[today] [at] h:mma',
        lastDay: '[yesterday] [at] h:mma',
        lastWeek: '[last] dddd [at] h:mma',
        sameElse: `[on] ${dateFormat} [at] h:mma`,
      });

  const auditInfo = isBlank(audit)
    ? ''
    : ` by ${audit.firstName} ${audit.lastName} ${dateStringToString(
        audit.timestamp,
      )}. Calculated value is ${formatter(source?.[calculatedKey])}.`;

  const auditTooltipText =
    toolTipText ??
    (auditInfo
      ? `Last edited${!isBlank(source?.[calculatedKey]) ? `${auditInfo}` : '.'}`
      : null);

  return (
    <CellContainer
      big={tableContext.big}
      dark={tableContext.dark}
      dim={!source}
      editable={source && isEditable}
      edited={highlightOverrides && edited}
      {...rest}
    >
      {isLoading ? (
        <Flex justifyContent="flex-end">
          <Spinner />
        </Flex>
      ) : !source ? (
        '---'
      ) : isEditable && editableComponent ? (
        React.createElement(
          editableComponent,
          {
            value,
            onBlur: (e, value) => handleSave(value),
            name,
            formatter,
            parser,
            validator,
            column,
            forceDefaultWhenBlank: shouldForceDefaultWhenBlank,
          },
          null,
        )
      ) : edited ? (
        <Tooltip placement="top" label={auditTooltipText}>
          <span>{formatter(value)}</span>
        </Tooltip>
      ) : (
        formatter(value)
      )}
      {shouldShowSubValue && (
        <Text
          mb={0.5}
          fontFamily="data"
          fontSize="sm"
          fontWeight="heavy"
          lineHeight="0.75rem"
          color="feature.secondary"
        >
          {unitFormatter(subValue)}
        </Text>
      )}
      {!isLoading && leftElement && (
        <Flex position="absolute" left={0} top={0} h="full" alignItems="center">
          {leftElement}
        </Flex>
      )}
      {shouldShowRevertIcon && (
        <Flex
          position="absolute"
          right={0}
          top={0}
          h="full"
          alignItems="center"
        >
          <Tooltip placement="top" label={auditTooltipText}>
            <IconButton
              m={0}
              bg="transparent"
              boxSize={2}
              _hover={{
                bg: 'transparent',
              }}
              _active={{
                bg: 'transparent',
              }}
              onClick={() => onSave(null)}
            >
              <UndoIcon viewBox="2 2 12 12" boxSize={2} />
            </IconButton>
          </Tooltip>
        </Flex>
      )}
    </CellContainer>
  );
};

export default Cell;
